import "react-dropzone-uploader/dist/styles.css";
import React, { useState, useMemo, useCallback } from "react";
import Dropzone from "react-dropzone-uploader";
import { Dialog } from "primereact/dialog";
import Loader from "./Loader";
import { postUploadFileManagementUrl } from "../service/ApiUrls";
import { ModuleNameEnum, UploadActionEnum } from "../Enums";
import LocalStorageService from "../service/localStorageService";
const qr = require("../assets/fileupload.png");

const Upload = (props) => {
  const [busy, setBusy] = useState(false);
  const [textToShow, setTextToShow] = useState("Uploading…");

  const fromEntApp = props?.from === "entApp";
  const fromContentPush = props?.from === "contentPush";
  const fromImeiUpload = props?.from === "imeiUpload";

  const token = useMemo(() => {
    const svc = LocalStorageService.getService?.();
    return svc?.getAccessToken?.();
  }, []);

  /** Build per-request fields based on props */
  const buildFields = useCallback(() => {
    if (fromEntApp) {
      return {
        uploadActionType: UploadActionEnum.APKPARSE,
        moduleName: ModuleNameEnum.APP,
      };
    }
    if (fromImeiUpload) {
      const f = {
        uploadActionType: UploadActionEnum.ONLYUPLOAD,
        moduleName: ModuleNameEnum.ENROLLMENT,
      };
      if (props?.mediaPath) f.mediaPath = props.mediaPath;
      if (props?.parentId != null) f.parentId = props.parentId;
      return f;
    }
    if (fromContentPush) {
      const f = {
        uploadActionType: UploadActionEnum.ONLYUPLOAD,
        moduleName: ModuleNameEnum.CONTENT,
      };
      if (props?.mediaPath) f.mediaPath = props.mediaPath;
      if (props?.parentId != null) f.parentId = props.parentId;

      // --- CHANGE START: Add divisionId to fields ---
      if (props?.divisionId) f.divisionId = props.divisionId;
      // --- CHANGE END ---

      return f;
    }
    // default
    return {
      uploadActionType: UploadActionEnum.ONLYUPLOAD,
      moduleName: ModuleNameEnum.CONTENT,
    };
  }, [
    fromEntApp,
    fromImeiUpload,
    fromContentPush,
    props?.mediaPath,
    props?.parentId,
    props?.divisionId
  ]);

  /** react-dropzone-uploader: return only { url, headers, fields } */
  const getUploadParams = useCallback(
    async ({ file, meta }) => {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const fields = buildFields();

      return {
        url: postUploadFileManagementUrl,
        headers,
        fields,
      };
    },
    [token, buildFields]
  );

  /** Status transitions from the library */
  const handleChangeStatus = useCallback(
    ({ meta, file, xhr }, status) => {
      // Show loader only for active statuses
      const activeStatuses = new Set([
        "preparing",
        "getting_upload_params",
        "uploading",
        "headers_received",
      ]);
      setBusy(activeStatuses.has(status));
      if (activeStatuses.has(status)) setTextToShow(status.replace(/_/g, " "));

      if (status === "error_upload" || status === "exception_upload") {
        const code = xhr?.status;
        const text = xhr?.responseText || xhr?.statusText;
        console.warn("[Upload error]", { status, httpStatus: code, responseText: text, meta });
      }

      if (status === "removed") {
        if (fromEntApp && props?.handleSubmit) props.handleSubmit({ result: null });
        return;
      }

      if (status === "done") {
        try {
          const parsed = xhr?.response ? JSON.parse(xhr.response) : null;

          if (fromEntApp && props?.handleSubmit) {
            props.handleSubmit(parsed || { ok: true });
          } else if (fromImeiUpload && props?.handleSubmit) {
            props.handleSubmit(parsed || { ok: true });
          } else if (fromContentPush && props?.handleSubmit) {
            const files = [{ file }];
            props.handleSubmit(parsed || { ok: true }, files, props?.mediaPath);
          }
        } catch (e) {
          console.error("Failed to parse upload response JSON", e);
        }
      }
    },
    [fromEntApp, fromImeiUpload, fromContentPush, props?.handleSubmit, props?.mediaPath]
  );

  const dropZoneProps = useMemo(() => {
    const common = {
      getUploadParams,
      onChangeStatus: handleChangeStatus,
      multiple: false,
      maxFiles: 1,
      // autoUpload: true, // default is true; uncomment if you ever change it
      // timeout: 0,       // set if you want to change default XHR timeout
    };

    if (fromEntApp) {
      return {
        ...common,
        accept: ".apk",
        maxSizeBytes: 20 * 1024 * 1024 * 1024,
        inputContent: (files, extra) =>
          (extra.reject ? "APK files only" : "Drag & drop or click") && (
            <img src={qr} alt="Upload" width="300" />
          ),
        styles: {
          dropzoneReject: { borderColor: "red", backgroundColor: "#DAA" },
          inputLabel: (files, extra) => (extra.reject ? { color: "red" } : {}),
        },
      };
    }

    if (fromImeiUpload) {
      return {
        ...common,
        accept: ".xls,.xlsx",
        inputContent: <img src={qr} alt="Upload" width="300" />,
      };
    }

    // default / content push
    return {
      ...common,
      // accept:
      //   ".webm,.jpg,.png,.mp4,.xlsx,.doc,.xls,.ppt,.dotx,.docx,.apk,.exe,.mp3,.pdf,.zip,.gif,.jpeg,.psd,.wmv,.avi,.mov,.ovpn,.sql,.log,.pptx,.txt,.csv",
      accept: "",
      maxSizeBytes: 20 * 1024 * 1024 * 1024,
      inputContent: <img src={qr} alt="Upload" width="300" />,
    };
  }, [fromEntApp, fromImeiUpload, getUploadParams, handleChangeStatus]);

  return (
    <>
      <Dialog
        visible={busy}
        modal
        style={{ width: "20vw" }}
        closable={false}
        showHeader={false}
        onHide={() => setBusy(false)}
      >
        <Loader textToShow={textToShow} />
      </Dialog>

      <Dropzone {...dropZoneProps} />
    </>
  );
};

export default Upload;